export type RequestConstants = {
  REQUEST: string;
  SUCCESS: string;
  ERROR: string;
};

export const createRequestConstants = (constant: string) =>
  ({
    REQUEST: `${constant}_REQUEST`,
    SUCCESS: `${constant}_SUCCESS`,
    ERROR: `${constant}_ERROR`,
  } as RequestConstants);

export type WSRequestConstants = RequestConstants & {
  REQUEST_SUCCESS: string;
  REQUEST_ERROR: string;
};

export const createWSRequestConstants = (constant: string) =>
  ({
    ...createRequestConstants(constant),
    REQUEST_SUCCESS: `${constant}_REQUEST_SUCCESS`,
    REQUEST_ERROR: `${constant}_REQUEST_ERROR`,
  } as WSRequestConstants);

export const LIS = {
  ACCESSION: {
    GET: createRequestConstants("@@lis/ACCESSION_GET"),
    CREATE: createRequestConstants("@@lis/ACCESSION_CREATE"),
    UPDATE: createRequestConstants("@@lis/ACCESSION_UPDATE"),
    DELETE: createRequestConstants("@@lis/ACCESSION_DELETE"),
    PRINT: createRequestConstants("@@lis/EVENT_PRINT_ACCESSION"),
  },

  CASE: {
    GET: createRequestConstants("@@lis/CASE_GET"),
    LIST: createRequestConstants("@@lis/CASE_LIST"),
  },

  MODMED_CASE: {
    GET: createRequestConstants("@@lis/MODMED_CASE_GET"),
  },

  DOCUMENT: {
    GET_CASE: createRequestConstants("@@lis/DOCUMENT_GET_CASE"),
    STATION_DOCUMENT: createRequestConstants("@@lis/DOCUMENT_STATION_DOCUMENT"),
  },

  WORK: {
    GET_CASE: createRequestConstants("@@lis/WORK_GET_CASE"),
  },

  SPECIMEN: {
    GET_CASE: createRequestConstants("@@lis/SPECIMEN_GET_CASE"),
    UPDATE: createRequestConstants("@@lis/SPECIMEN_UPDATE"),
    SCAN: createRequestConstants("@@lis/EVENT_SCAN_SPECIMEN"),
    PRINT: createRequestConstants("@@lis/EVENT_PRINT_SPECIMEN"),
    PRINT_PIECE_COUNT: createRequestConstants(
      "@@lis/EVENT_PRINT_PIECE_COUNT_SPECIMEN"
    ),
  },

  BLOCK: {
    GET_CASE: createRequestConstants("@@lis/BLOCK_GET_CASE"),
    SCAN: createRequestConstants("@@lis/EVENT_SCAN_BLOCK"),
    PRINT: createRequestConstants("@@lis/EVENT_PRINT_BLOCK"),
    BATCH_PRINT: createRequestConstants("@@lis/EVENT_BATCH_PRINT_BLOCK"),
    CREATE: createRequestConstants("@@lis/BLOCK_CREATE"),
    UPDATE: createRequestConstants("@@lis/BLOCK_PARTIAL_UPDATE"),
    DESTROY: createRequestConstants("@@lis/BLOCK_DESTROY"),
    PRINT_CASSETTES: createWSRequestConstants("@@lis/BLOCK_PRINT_CASSETTES"),
  },

  SLIDE: {
    GET_CASE: createRequestConstants("@@lis/SLIDE_GET_CASE"),
    SCAN: createRequestConstants("@@lis/EVENT_SCAN_SLIDE"),
    PRINT_ADDITIONAL: createRequestConstants("@@lis/SLIDE_PRINT_ADDITIONAL"),
  },
};

// CASE_WITH_WORK_ACTIONS is an array of action constants which are expected to have LISCase objects as their action.payload.response
// LIS.ACCESSION.PRINT.SUCCESS is not included here because it's response type is different
export const CASE_WITH_WORK_ACTIONS: string[] = [
  LIS.CASE.GET.SUCCESS,
  LIS.MODMED_CASE.GET.SUCCESS,
  LIS.DOCUMENT.GET_CASE.SUCCESS,
  LIS.WORK.GET_CASE.SUCCESS,
  LIS.SPECIMEN.GET_CASE.SUCCESS,
  LIS.SPECIMEN.SCAN.SUCCESS,
  LIS.SPECIMEN.PRINT_PIECE_COUNT.SUCCESS,
  LIS.SPECIMEN.PRINT.SUCCESS,
  LIS.BLOCK.GET_CASE.SUCCESS,
  LIS.BLOCK.SCAN.SUCCESS,
  LIS.BLOCK.PRINT.SUCCESS,
  LIS.BLOCK.CREATE.SUCCESS,
  LIS.BLOCK.DESTROY.SUCCESS,
  LIS.SLIDE.GET_CASE.SUCCESS,
  LIS.SLIDE.SCAN.SUCCESS,
  LIS.SLIDE.PRINT_ADDITIONAL.SUCCESS,
];

export const SET_ACCESSION_PROCESSING_ID =
  "@@accession/SET_ACCESSION_PROCESSING_ID";

export const QR_LOGIN_CREATE_REQUEST = "@@auth/QR_LOGIN_CREATE_REQUEST";
export const QR_LOGIN_CREATE_SUCCESS = "@@auth/QR_LOGIN_CREATE_SUCCESS";
export const QR_LOGIN_CREATE_ERROR = "@@auth/QR_LOGIN_CREATE_ERROR";

export const MESSAGES_REQUEST = "@@messages/MESSAGES_REQUEST";
export const MESSAGES_SUCCESS = "@@messages/MESSAGES_SUCCESS";
export const MESSAGES_FAILURE = "@@messages/MESSAGES_FAILURE";
export const DISCLAIMERS_REQUEST = "@@disclaimers/DISCLAIMERS_REQUEST";
export const DISCLAIMERS_FAILURE = "@@disclaimers/DISCLAIMERS_FAILURE";
export const DISCLAIMERS_SUCCESS = "@@disclaimers/DISCLAIMERS_SUCCESS";
export const AI_OUTPUT_REQUEST = "@@ai/AI_OUTPUT_REQUEST";
export const AI_OUTPUT_FAILURE = "@@ai/AI_OUTPUT_FAILURE";
export const AI_OUTPUT_SUCCESS = "@@ai/AI_OUTPUT_SUCCESS";
export const SPECIMEN_CLASSIFICATION_REQUEST =
  "@@ai/SPECIMEN_CLASSIFICATION_REQUEST";
export const SPECIMEN_CLASSIFICATION_FAILURE =
  "@@ai/SPECIMEN_CLASSIFICATION_FAILURE";
export const SPECIMEN_CLASSIFICATION_SUCCESS =
  "@@ai/SPECIMEN_CLASSIFICATION_SUCCESS";
export const SLIDE_CLASSIFICATION_REQUEST = "@@ai/SLIDE_CLASSIFICATION_REQUEST";
export const SLIDE_CLASSIFICATION_FAILURE = "@@ai/SLIDE_CLASSIFICATION_FAILURE";
export const SLIDE_CLASSIFICATION_SUCCESS = "@@ai/SLIDE_CLASSIFICATION_SUCCESS";
export const WIPE_AI = "@@ai/WIPE_AI";
export const CASE_BY_ACCESSION_REQUEST =
  "@@slide_detail/CASE_BY_ACCESSION_REQUEST";
export const CASE_BY_ACCESSION_SUCCESS =
  "@@slide_detail/CASE_BY_ACCESSION_SUCCESS";
export const CASE_BY_ACCESSION_FAILURE =
  "@@slide_detail/CASE_BY_ACCESSION_FAILURE";
export const CASES_FAILURE = "@@case/CASES_FAILURE";
export const CASES_REQUEST = "@@case/CASES_REQUEST";
export const CASES_SUCCESS = "@@case/CASES_SUCCESS";
export const DOCUMENTS_LIST_FAILURE = "@@documentsList/DOCUMENTS_LIST_FAILURE";
export const DOCUMENTS_LIST_SUCCESS = "@@documentsList/DOCUMENTS_LIST_SUCCESS";
export const DOCUMENTS_LIST_REQUEST = "@@documentsList/DOCUMENTS_LIST_REQUEST";
export const SET_LAST_CASE_VIEWED = "@@case/SET_LAST_CASE_VIEWED";
export const CASE_ID_LIST_REQUEST = "@@case/CASE_ID_LIST_REQUEST";
export const CASE_ID_LIST_FAILURE = "@@case/CASE_ID_LIST_FAILURE";
export const CASE_ID_LIST_SUCCESS = "@@case/CASE_ID_LIST_SUCCESS";
// NAVIGATE_CASE_LIST_ASYNC_REQUEST, _SUCCESS, etc. have been purged
export const NAVIGATE_CASE_LIST_SYNC = "@@case/NAVIGATE_CASE_LIST_SYNC";
export const WIPE_CASES = "@@case/WIPE_CASES";
export const CASE_DETAIL_FAILURE = "@@case_detail/CASE_DETAIL_FAILURE";
export const CASE_DETAIL_REQUEST = "@@case_detail/CASE_DETAIL_REQUEST";
export const CASE_DETAIL_SUCCESS = "@@case_detail/CASE_DETAIL_SUCCESS";
export const REVIEWS_REQUEST = "@@case_detail/REVIEWS_REQUEST";
export const REVIEWS_SUCCESS = "@@case_detail/REVIEWS_SUCCESS";
export const REVIEWS_FAILURE = "@@case_detail/REVIEWS_FAILURE";
export const CREATE_NOTE_REQUEST = "@@case_detail/CREATE_NOTE_REQUEST";
export const CREATE_NOTE_SUCCESS = "@@case_detail/CREATE_NOTE_SUCCESS";
export const CREATE_NOTE_FAILURE = "@@case_detail/CREATE_NOTE_FAILURE";
export const CASE_DETAIL_EVENTS_FAILURE =
  "@@case_detail/CASE_DETAIL_EVENTS_FAILURE";
export const CASE_DETAIL_EVENTS_REQUEST =
  "@@case_detail/CASE_DETAIL_EVENTS_REQUEST";
export const CASE_DETAIL_EVENTS_SUCCESS =
  "@@case_detail/CASE_DETAIL_EVENTS_SUCCESS";

export const CASE_REQUEST_SCREEN_REQUEST =
  "@@case_detail/CASE_REQUEST_SCREEN_REQUEST";
export const CASE_REQUEST_SCREEN_SUCCESS =
  "@@case_detail/CASE_REQUEST_SCREEN_SUCCESS";
export const CASE_REQUEST_SCREEN_FAILURE =
  "@@case_detail/CASE_REQUEST_SCREEN_FAILURE";

export const CASE_UNDO_SCREEN_REQUEST =
  "@@case_detail/CASE_UNDO_SCREEN_REQUEST";
export const CASE_UNDO_SCREEN_SUCCESS =
  "@@case_detail/CASE_UNDO_SCREEN_SUCCESS";
export const CASE_UNDO_SCREEN_FAILURE =
  "@@case_detail/CASE_UNDO_SCREEN_FAILURE";

export const CASE_PATIENT_MATCHES_REQUEST =
  "@@case_detail/CASE_PATIENT_MATCHES_REQUEST";
export const CASE_PATIENT_MATCHES_SUCCESS =
  "@@case_detail/CASE_PATIENT_MATCHES_SUCCESS";
export const CASE_PATIENT_MATCHES_FAILURE =
  "@@case_detail/CASE_PATIENT_MATCHES_FAILURE";
export const COPATH_CASE_PATIENT_MATCHES_REQUEST =
  "@@case_detail/COPATH_CASE_PATIENT_MATCHES_REQUEST";
export const COPATH_CASE_PATIENT_MATCHES_SUCCESS =
  "@@case_detail/COPATH_CASE_PATIENT_MATCHES_SUCCESS";
export const COPATH_CASE_PATIENT_MATCHES_FAILURE =
  "@@case_detail/COPATH_CASE_PATIENT_MATCHES_FAILURE";

export const CASE_OPEN_DOCUMENT_REQUEST =
  "@@case_detail/CASE_OPEN_DOCUMENT_REQUEST";
export const CASE_OPEN_DOCUMENT_SUCCESS =
  "@@case_detail/CASE_OPEN_DOCUMENT_SUCCESS";
export const CASE_OPEN_DOCUMENT_FAILURE =
  "@@case_detail/CASE_OPEN_DOCUMENT_FAILURE";
export const CASE_OPEN_DOCUMENT_MISSING =
  "@@case_detail/CASE_OPEN_DOCUMENT_MISSING";

export const CASE_OPEN_SUPPORT_DOCUMENT_REQUEST =
  "@@case_detail/CASE_OPEN_SUPPORT_DOCUMENT_REQUEST";
export const CASE_OPEN_SUPPORT_DOCUMENT_SUCCESS =
  "@@case_detail/CASE_OPEN_SUPPORT_DOCUMENT_SUCCESS";
export const CASE_OPEN_SUPPORT_DOCUMENT_FAILURE =
  "@@case_detail/CASE_OPEN_SUPPORT_DOCUMENT_FAILURE";

export const GET_MACRO_HELPERS_REQUEST =
  "@@case_detail/GET_MACRO_HELPERS_REQUEST";
export const GET_MACRO_HELPERS_SUCCESS =
  "@@case_detail/GET_MACRO_HELPERS_SUCCESS";
export const GET_MACRO_HELPERS_ERROR = "@@case_detail/GET_MACRO_HELPERS_ERROR";

export const UPDATE_CASE_ADDON_REQUEST =
  "@@case_detail/UPDATE_CASE_ADDON_REQUEST";
export const UPDATE_CASE_ADDON_SUCCESS =
  "@@case_detail/UPDATE_CASE_ADDON_SUCCESS";
export const UPDATE_CASE_ADDON_FAILURE =
  "@@case_detail/UPDATE_CASE_ADDON_FAILURE";

export const CASE_REPORT_REQUEST = "@@case_report/CASE_REPORT_REQUEST";
export const CASE_REPORT_SUCCESS = "@@case_report/CASE_REPORT_SUCCESS";
export const CASE_REPORT_FAILURE = "@@case_report/CASE_REPORT_FAILURE";

export const CASE_DISCLAIMERS_REQUEST =
  "@@case_report/CASE_DISCLAIMERS_REQUEST";
export const CASE_DISCLAIMERS_SUCCESS =
  "@@case_report/CASE_DISCLAIMERS_SUCCESS";
export const CASE_DISCLAIMERS_FAILURE =
  "@@case_report/CASE_DISCLAIMERS_FAILURE";

export const CASE_DIAGNOSES_REQUEST = "@@case_detail/CASE_DIAGNOSES_REQUEST";
export const CASE_DIAGNOSES_FAILURE = "@@case_detail/CASE_DIAGNOSES_FAILURE";
export const CASE_DIAGNOSES_SUCCESS = "@@case_detail/CASE_DIAGNOSES_SUCCESS";

export const DIAGNOSE_REQUEST = "@@case_detail/DIAGNOSE_REQUEST";
export const DIAGNOSE_FAILURE = "@@case_detail/DIAGNOSE_FAILURE";
export const DIAGNOSE_SUCCESS = "@@case_detail/DIAGNOSE_SUCCESS";

export const UPDATE_DIAGNOSIS_REQUEST =
  "@@case_detail/UPDATE_DIAGNOSIS_REQUEST";
export const UPDATE_DIAGNOSIS_FAILURE =
  "@@case_detail/UPDATE_DIAGNOSIS_FAILURE";
export const UPDATE_DIAGNOSIS_SUCCESS =
  "@@case_detail/UPDATE_DIAGNOSIS_SUCCESS";

export const DELETE_DIAGNOSIS_REQUEST = "@@diagnosis/DELETE_DIAGNOSIS_REQUEST";
export const DELETE_DIAGNOSIS_FAILURE = "@@diagnosis/DELETE_DIAGNOSIS_FAILURE";
export const DELETE_DIAGNOSIS_SUCCESS = "@@diagnosis/DELETE_DIAGNOSIS_SUCCESS";

export const REPORT_SIGNEE_REQUEST = "@@case_detail/REPORT_SIGNEE_REQUEST";
export const REPORT_SIGNEE_SUCCESS = "@@case_detail/REPORT_SIGNEE_SUCCESS";
export const REPORT_SIGNEE_FAILURE = "@@case_detail/REPORT_SIGNEE_FAILURE";

export const MODIFY_REPORT_REQUEST = "@@case_detail/MODIFY_REPORT_REQUEST";
export const MODIFY_REPORT_SUCCESS = "@@case_detail/MODIFY_REPORT_SUCCESS";
export const MODIFY_REPORT_FAILURE = "@@case_detail/MODIFY_REPORT_FAILURE";

export const CANCEL_MODIFY_REPORT_REQUEST =
  "@@case_detail/CANCEL_MODIFY_REPORT_REQUEST";
export const CANCEL_MODIFY_REPORT_SUCCESS =
  "@@case_detail/CANCEL_MODIFY_REPORT_SUCCESS";
export const CANCEL_MODIFY_REPORT_FAILURE =
  "@@case_detail/CANCEL_MODIFY_REPORT_FAILURE";

export const EDIT_SCREEN_DIAGNOSES_REQUEST =
  "@@case_detail/EDIT_SCREEN_DIAGNOSES_REQUEST";
export const EDIT_SCREEN_DIAGNOSES_SUCCESS =
  "@@case_detail/EDIT_SCREEN_DIAGNOSES_SUCCESS";
export const EDIT_SCREEN_DIAGNOSES_FAILURE =
  "@@case_detail/EDIT_SCREEN_DIAGNOSES_FAILURE";

export const CANCEL_EDIT_SCREEN_DIAGNOSES_REQUEST =
  "@@case_detail/CANCEL_EDIT_SCREEN_DIAGNOSES_REQUEST";
export const CANCEL_EDIT_SCREEN_DIAGNOSES_SUCCESS =
  "@@case_detail/CANCEL_EDIT_SCREEN_DIAGNOSES_SUCCESS";
export const CANCEL_EDIT_SCREEN_DIAGNOSES_FAILURE =
  "@@case_detail/CANCEL_EDIT_SCREEN_DIAGNOSES_FAILURE";

export const RESET_CODE_SEARCHES = "@@case_detail/RESET_CODE_SEARCHES";

export const CASE_SIGN_OUT_REQUEST = "@@case_detail/CASE_SIGN_OUT_REQUEST";
export const CASE_SIGN_OUT_FAILURE = "@@case_detail/CASE_SIGN_OUT_FAILURE";
export const CASE_SIGN_OUT_SUCCESS = "@@case_detail/CASE_SIGN_OUT_SUCCESS";
export const CASE_RECALL_REPORT_REQUEST =
  "@@case_detail/CASE_RECALL_REPORT_REQUEST";
export const CASE_RECALL_REPORT_FAILURE =
  "@@case_detail/CASE_RECALL_REPORT_FAILURE";
export const CASE_RECALL_REPORT_SUCCESS =
  "@@case_detail/CASE_RECALL_REPORT_SUCCESS";

export const CASE_PATCH_REQUEST = "@@case_detail/CASE_PATCH_REQUEST,";
export const CASE_PATCH_SUCCESS = "@@case_detail/CASE_PATCH_SUCCESS,";
export const CASE_PATCH_FAILURE = "@@case_detail/CASE_PATCH_FAILURE,";
export const CASE_PATCH_STATUS_REQUEST =
  "@@case_detail/CASE_PATCH_STATUS_REQUEST";
export const CASE_PATCH_STATUS_SUCCESS =
  "@@case_detail/CASE_PATCH_STATUS_SUCCESS";
export const CASE_PATCH_STATUS_FAILURE =
  "@@case_detail/CASE_PATCH_STATUS_FAILURE";

export const NAVIGATE_CASE_LIST_END_OF_LIST =
  "@@case/NAVIGATE_CASE_LIST_END_OF_LIST";

export const WIPE_CASE_DETAIL = "@@case_detail/WIPE_CASE_DETAIL";

export const RESET_AUTH_STATE = "@@auth/RESET_AUTH_STATE";
export const DEAUTHENTICATE = "@@auth/DEAUTHENTICATE";
export const LOGIN_FAILURE = "@@auth/LOGIN_FAILURE";
export const LOGIN_REQUEST = "@@auth/LOGIN_REQUEST";
export const LOGIN_SUCCESS = "@@auth/LOGIN_SUCCESS";
export const TOKEN_FAILURE = "@@auth/TOKEN_FAILURE";
export const TOKEN_RECEIVED = "@@auth/TOKEN_RECEIVED";
export const TOKEN_REQUEST = "@@auth/TOKEN_REQUEST";
export const TOKEN_PAIR_REQUEST = "@@auth/TOKEN_PAIR_REQUEST";
export const RESTORE_AUTH_STATE = "@@auth/RESTORE_AUTH_STATE";
export const LOGIN_BARCODE = "@auth/LOGIN_BARCODE";
export const RESET_AUTH_ERRORS = "@auth/RESET_AUTH_ERRORS";
export const MFA_LOGIN_GET_CODE_REQUEST = "@@auth/MFA_LOGIN_GET_CODE_REQUEST";
export const MFA_GET_CODE_REQUEST = "@@auth/MFA_GET_CODE_REQUEST";
export const MFA_GET_CODE_SUCCESS = "@@auth/MFA_GET_CODE_SUCCESS";
export const MFA_GET_CODE_FAILURE = "@@auth/MFA_GET_CODE_FAILURE";
export const MFA_VERIFY_CODE_REQUEST = "@@auth/MFA_VERIFY_CODE_REQUEST";
export const MFA_VERIFY_CODE_SUCCESS = "@@auth/MFA_VERIFY_CODE_SUCCESS";
export const MFA_VERIFY_CODE_FAILURE = "@@auth/MFA_VERIFY_CODE_FAILURE";
export const MFA_SELECT_METHOD = "@@auth/MFA_SELECT_METHOD";

export const SLIDE_DETAIL_FAILURE = "@@slide_detail/SLIDE_DETAIL_FAILURE";
export const SLIDE_DETAIL_REQUEST = "@@slide_detail/SLIDE_DETAIL_REQUEST";
export const SLIDE_DETAIL_SUCCESS = "@@slide_detail/SLIDE_DETAIL_SUCCESS";
export const SLIDE_BARCODE_URL_REQUEST =
  "@@slide_detail/SLIDE_BARCODE_URL_REQUEST";
export const SLIDE_BARCODE_URL_FAILURE =
  "@@slide_detail/SLIDE_BARCODE_URL_FAILURE";
export const SLIDE_BARCODE_URL_SUCCESS =
  "@@slide_detail/SLIDE_BARCODE_URL_SUCCESS";
export const SLIDE_MACRO_URL_REQUEST = "@@slide_detail/SLIDE_MACRO_URL_REQUEST";
export const SLIDE_MACRO_URL_FAILURE = "@@slide_detail/SLIDE_MACRO_URL_FAILURE";
export const SLIDE_MACRO_URL_SUCCESS = "@@slide_detail/SLIDE_MACRO_URL_SUCCESS";
export const CHANGE_VIEWER_MODE = "@@slide_detail/CHANGE_VIEWER_MODE";
export const FETCH_ANNOTATIONS_REQUEST =
  "@@slide_detail/FETCH_ANNOTATIONS_REQUEST";
export const FETCH_ANNOTATIONS_FAILURE =
  "@@slide_detail/FETCH_ANNOTATIONS_FAILURE";
export const FETCH_ANNOTATIONS_SUCCESS =
  "@@slide_detail/FETCH_ANNOTATIONS_SUCCESS";
export const ADD_ANNOTATION_REQUEST = "@@slide_detail/ADD_ANNOTATION_REQUEST";
export const ADD_ANNOTATION_FAILURE = "@@slide_detail/ADD_ANNOTATION_FAILURE";
export const ADD_ANNOTATION_SUCCESS = "@@slide_detail/ADD_ANNOTATION_SUCCESS";
export const SET_EDITED_ANNOTATION = "@@slide_detail/SET_EDITED_ANNOTATION";
export const EDIT_ANNOTATION_REQUEST = "@@slide_detail/EDIT_ANNOTATION_REQUEST";
export const EDIT_ANNOTATION_FAILURE = "@@slide_detail/EDIT_ANNOTATION_FAILURE";
export const EDIT_ANNOTATION_SUCCESS = "@@slide_detail/EDIT_ANNOTATION_SUCCESS";
export const TOGGLE_ANNOTATION_VISIBILITY =
  "@@slide_detail/TOGGLE_ANNOTATION_VISIBILITY";
export const CLEAR_MINOR_ERROR = "@@slide_detail/CLEAR_MINOR_ERROR";
export const DELETE_ANNOTATION_REQUEST =
  "@@slide_detail/DELETE_ANNOTATION_REQUEST";
export const DELETE_ANNOTATION_FAILURE =
  "@@slide_detail/DELETE_ANNOTATION_FAILURE";
export const DELETE_ANNOTATION_SUCCESS =
  "@@slide_detail/DELETE_ANNOTATION_SUCCESS";
export const START_NEW_PEN_DRAWING = "@@slide_detail/START_NEW_PEN_DRAWING";
export const RESET_NEW_PEN_DRAWING = "@@slide_detail/RESET_NEW_PEN_DRAWING";
export const SELECT_ANNOTATION = "@@slide_detail/SELECT_ANNOTATION";
export const SHOW_MASK = "@@slide_detail/SHOW_MASK";
export const WIPE_SLIDE_DETAIL = "@@slide_detail/WIPE_SLIDE_DETAIL";
export const SYSTEM_HEARTBEAT = "@@sys/SYSTEM_HEARTBEAT";
export const SYSTEM_DICTATION_SET_KEY = "@@sys/SYSTEM_DICTATION_SET_KEY";
export const SYSTEM_DICTATION_SET_LOADING_PROGRESS =
  "@@sys/SYSTEM_DICTATION_SET_LOADING_PROGRESS";
export const SYSTEM_DICTATION_THROTTLED_LOADING_PROGRESS =
  "@@sys/SYSTEM_DICTATION_THROTTLED_LOADING_PROGRESS";
export const SYSTEM_DICTATION_SET_GLOBAL_STATUS =
  "@@sys/SYSTEM_DICTATION_SET_GLOBAL_STATUS";
export const SYSTEM_DICTATION_SET_TEXT = "@@sys/SYSTEM_DICTATION_SET_TEXT";
export const SYSTEM_DICTATION_TOGGLE = "@@sys/SYSTEM_DICTATION_TOGGLE";
export const SYSTEM_FEATURE_FLAGS_REQUEST =
  "@@sys/SYSTEM_FEATURE_FLAGS_REQUEST";
export const SYSTEM_FEATURE_FLAGS_SUCCESS =
  "@@sys/SYSTEM_FEATURE_FLAGS_SUCCESS";
export const SYSTEM_FEATURE_FLAGS_FAILURE =
  "@@sys/SYSTEM_FEATURE_FLAGS_FAILURE";
export const BROWSER_FEATURES_SET_AVIF_SUPPORTED =
  "@@sys/BROWSER_FEATURES_SET_AVIF_SUPPORTED";
export const USER_DETAIL_FAILURE = "@@user/USER_DETAIL_FAILURE";
export const USER_DETAIL_REQUEST = "@@user/USER_DETAIL_REQUEST";
export const USER_DETAIL_SUCCESS = "@@user/USER_DETAIL_SUCCESS";
export const USER_UNSET_MFA_METHOD_FAILURE =
  "@@user/USER_UNSET_MFA_METHOD_FAILURE";
export const USER_UNSET_MFA_METHOD_REQUEST =
  "@@user/USER_UNSET_MFA_METHOD_REQUEST";
export const USER_UNSET_MFA_METHOD_SUCCESS =
  "@@user/USER_UNSET_MFA_METHOD_SUCCESS";
export const USER_ME_FAILURE = "@@user/USER_ME_FAILURE";
export const USER_ME_REQUEST = "@@user/USER_ME_REQUEST";
export const USER_ME_SUCCESS = "@@user/USER_ME_SUCCESS";
export const WIPE_PW_RESET = "@@user/WIPE_PW_RESET";
export const PW_RESET_FAILURE = "@@user/PW_RESET_FAILURE";
export const PW_RESET_REQUEST = "@@user/PW_RESET_REQUEST";
export const PW_RESET_SUCCESS = "@@user/PW_RESET_SUCCESS";
export const ADD_TOAST = "@@user/ADD_TOAST";
export const REMOVE_TOAST = "@@user/REMOVE_TOAST";
export const WIPE_PW_RESET_CONFIRM = "@@user/WIPE_PW_RESET_CONFIRM";
export const PW_RESET_CONFIRM_FAILURE = "@@user/PW_RESET_CONFIRM_FAILURE";
export const PW_RESET_CONFIRM_REQUEST = "@@user/PW_RESET_CONFIRM_REQUEST";
export const PW_RESET_CONFIRM_SUCCESS = "@@user/PW_RESET_CONFIRM_SUCCESS";

export const SAVE_PROFILE_REQUEST = "@@user/SAVE_PROFILE_REQUEST";

export const SET_PROFILE_FAILURE = "@@user/SET_PROFILE_FAILURE";
export const SET_PROFILE_SUCCESS = "@@user/SET_PROFILE_SUCCESS";
export const SET_PROFILE_SKIP = "@@user/SET_PROFILE_SKIP";
export const SET_PROFILE_RESET_ERRORS = "@@user/SET_PROFILE_RESET_ERRORS";
export const AUTHORIZED_SET_PASSWORD_FAILURE =
  "@@user/AUTHORIZED_SET_PASSWORD_FAILURE";
export const AUTHORIZED_SET_PASSWORD_SUCCESS =
  "@@user/AUTHORIZED_SET_PASSWORD_SUCCESS";

export const SLIDE_REQUEST_RESCAN_FAILURE =
  "@@slideDetail/SLIDE_REQUEST_RESCAN_FAILURE";
export const SLIDE_REQUEST_RESCAN_REQUEST =
  "@@slideDetail/SLIDE_REQUEST_RESCAN_REQUEST";
export const SLIDE_REQUEST_RESCAN_SUCCESS =
  "@@slideDetail/SLIDE_REQUEST_RESCAN_SUCCESS";

export const SLIDE_BARCODE_SCAN_REQUEST =
  "@@slideDetail/SLIDE_BARCODE_SCAN_REQUEST";
export const SLIDE_BARCODE_SCAN_SUCCESS =
  "@@slideDetail/SLIDE_BARCODE_SCAN_SUCCESS";
export const SLIDE_BARCODE_SCAN_FAILURE =
  "@@slideDetail/SLIDE_BARCODE_SCAN_FAILURE";

export const OSD_FULLY_LOADED_CHANGE = "@@osd/FULLY_LOADED_CHANGE";
export const SLIDE_TILES_LOADED = "@@slides/SLIDE_TILES_LOADED";

export const SLIDE_RANGE_MANIFEST_REQUEST =
  "@@slides/SLIDE_RANGE_MANIFEST_REQUEST";
export const SLIDE_RANGE_MANIFEST_SUCCESS =
  "@@slides/SLIDE_RANGE_MANIFEST_SUCCESS";
export const SLIDE_RANGE_MANIFEST_FAILURE =
  "@@slides/SLIDE_RANGE_MANIFEST_FAILURE";

export const SET_CASE_SEARCH = "@@case/SET_CASE_SEARCH";
export const WIPE_CASES_STATUS_COUNT = "@@case/WIPE_STATUS_COUNT";
export const USERS_LIST_SUCCESS = "@@users/USERS_LIST_SUCCESS";
export const USERS_LIST_REQUEST = "@@users/USERS_LIST_REQUEST";
export const USERS_LIST_FAILURE = "@@users/USERS_LIST_FAILURE";

export const USER_SIGNATURES_REQUEST = "@@signatures/USER_SIGNATURES_REQUEST";
export const USER_SIGNATURES_FAILURE = "@@signatures/USER_SIGNATURES_FAILURE";
export const USER_SIGNATURES_SUCCESS = "@@signatures/USER_SIGNATURES_SUCCESS";

export const LABORDERS_LIST_REQUEST = "@@orders/LABORDERS_LIST_REQUEST";
export const LABORDERS_LIST_SUCCESS = "@@orders/LABORDERS_LIST_SUCCESS";
export const LABORDERS_LIST_FAILURE = "@@orders/LABORDERS_LIST_FAILURE";
export const ASSESSMENT_REQUEST = "@@assessment/ASSESSMENT_REQUEST";
export const ASSESSMENT_SUCCESS = "@@assessment/ASSESSMENT_SUCCESS";
export const ASSESSMENT_FAILURE = "@@assessment/ASSESSMENT_FAILURE";

export const STAIN_ORDER_REQUEST = "@@case_detail/STAIN_ORDER_REQUEST";
export const STAIN_ORDER_FAILURE = "@@case_detail/STAIN_ORDER_FAILURE";
export const STAIN_ORDER_SUCCESS = "@@case_detail/STAIN_ORDER_SUCCESS";
export const COMPLETE_STAIN_ORDER_REQUEST =
  "@@case_detail/COMPLETE_STAIN_ORDER_REQUEST";
export const COMPLETE_STAIN_ORDER_SUCCESS =
  "@@case_detail/COMPLETE_STAIN_ORDER_SUCCESS";
export const COMPLETE_STAIN_ORDER_FAILURE =
  "@@case_detail/COMPLETE_STAIN_ORDER_FAILURE";
export const DELETE_STAIN_ORDER_REQUEST =
  "@@case_detail/DELETE_STAIN_ORDER_REQUEST";
export const DELETE_STAIN_ORDER_SUCCESS =
  "@@case_detail/DELETE_STAIN_ORDER_SUCCESS";
export const DELETE_STAIN_ORDER_FAILURE =
  "@@case_detail/DELETE_STAIN_ORDER_FAILURE";
export const PARTIAL_UPDATE_STAIN_ORDER_REQUEST =
  "@@case_detail/PARTIAL_UPDATE_ORDER_REQUEST";
export const PARTIAL_UPDATE_STAIN_ORDER_SUCCESS =
  "@@case_detail/PARTIAL_UPDATE_STAIN_ORDER_SUCCESS";
export const PARTIAL_UPDATE_STAIN_ORDER_FAILURE =
  "@@case_detail/PARTIAL_UPDATE_STAIN_ORDER_FAILURE";

export const OPTIMISTICALLY_EDIT_DIAGNOSIS =
  "@@specimenDetail/OPTIMISTICALLY_EDIT_DIAGNOSIS";
export const SET_MACRO_CODE = "@@specimenDetail/SET_MACRO_CODE";

export const FLAG_CREATE_REQUEST = "@@flags/FLAG_CREATE_REQUEST";
export const FLAG_MISLABELED_CREATE_REQUEST =
  "@@flags/FLAG_MISLABELED_CREATE_REQUEST";
export const FLAG_CREATE_FAILURE = "@@flags/FLAG_CREATE_FAILURE";
export const FLAG_CREATE_SUCCESS = "@@flags/FLAG_CREATE_SUCCESS";
export const FLAG_DELETE_REQUEST = "@@flags/FLAG_DELETE_REQUEST";
export const FLAG_DELETE_FAILURE = "@@flags/FLAG_DELETE_FAILURE";
export const FLAG_DELETE_SUCCESS = "@@flags/FLAG_DELETE_SUCCESS";
export const FLAGS_FETCH_REQUEST = "@@flags/FLAGS_FETCH_REQUEST";
export const FLAGS_FETCH_FAILURE = "@@flags/FLAGS_FETCH_FAILURE";
export const FLAGS_FETCH_SUCCESS = "@@flags/FLAGS_FETCH_SUCCESS";

export const STAINS_LIST_FAILURE = "@@stains/STAINS_LIST_FAILURE";
export const STAINS_LIST_REQUEST = "@@stains/STAINS_LIST_REQUEST";
export const STAINS_LIST_SUCCESS = "@@stains/STAINS_LIST_SUCCESS";
export const LABSTAINS_LIST_FAILURE = "@@stains/LABSTAINS_LIST_FAILURE";
export const LABSTAINS_LIST_REQUEST = "@@stains/LABSTAINS_LIST_REQUEST";
export const LABSTAINS_LIST_SUCCESS = "@@stains/LABSTAINS_LIST_SUCCESS";
export const LABSTAINS_LIST_CLEAR = "@@stains/LABSTAINS_LIST_CLEAR";
export const LABS_LIST_REQUEST = "@@labs/LABS_LIST_REQUEST";
export const LABS_LIST_SUCCESS = "@@labs/LABS_LIST_SUCCESS";
export const LABS_LIST_FAILURE = "@@labs/LABS_LIST_FAILURE";
export const UPDATE_CASE_SPECIMEN_FAILURE =
  "@@case_detail/UPDATE_CASE_SPECIMEN_FAILURE";
export const UPDATE_CASE_SPECIMEN_REQUEST =
  "@@case_detail/UPDATE_CASE_SPECIMEN_REQUEST";
export const UPDATE_CASE_SPECIMEN_SUCCESS =
  "@@case_detail/UPDATE_CASE_SPECIMEN_SUCCESS";
export const UPDATE_CASE_DOCUMENT_FAILURE =
  "@@case_detail/UPDATE_CASE_DOCUMENT_FAILURE";
export const UPDATE_CASE_DOCUMENT_REQUEST =
  "@@case_detail/UPDATE_CASE_DOCUMENT_REQUEST";
export const UPDATE_CASE_DOCUMENT_SUCCESS =
  "@@case_detail/UPDATE_CASE_DOCUMENT_SUCCESS";

// Websocket Message constants
export const INITIAL_MESSAGE = "@@web_socket/INITIAL_MESSAGE";
export const UPDATE_TASK_MESSAGE = "@@web_socket/UPDATE_TASK_MESSAGE";
export const REMOVE_TASK_MESSAGE = "@@web_socket/REMOVE_TASK_MESSAGE";

// DRY up creation of front-loader constants
const frontLoaderTypes = [
  "USERS",
  "LABS",
  "ACCESSION_PREFIXES",
  "LAB_STATIONS",
  "CLINICS",
  "LAB_CLINICS",
  "PATIENTS",
  "ACCESSIONS",
  "CASES",
  "WORK_PROCESSES",
  "WORK_PROCESS_TASKS",
  "STAINS",
  "LAB_STAINS",
  "EXTERNAL_LABS",
  "EXTERNAL_LAB_STAINS",
  "LAB_PANELS",
  "ANTIGEN_RETRIEVAL_METHODS",
  "ANTIBODY_SPECIES",
];
export const frontLoaderConstants = frontLoaderTypes.reduce(
  (constants: { [key: string]: string }, type: string) => {
    const requestConstant = `${type}_REQUEST`;
    const successConstant = `${type}_SUCCESS`;
    const failureConstant = `${type}_FAILURE`;
    return {
      ...constants,
      [requestConstant]: `@@front_loader/${requestConstant}`,
      [successConstant]: `@@front_loader/${successConstant}`,
      [failureConstant]: `@@front_loader/${failureConstant}`,
    };
  },
  {}
);
